import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useAuth } from "./AuthContext";

dayjs.extend(utc);
dayjs.extend(timezone);

type PreferenceContextType = {
  timezone: string;                  
  setTimezone: (tz: string) => void;
  setDateFormatPreference: (fmt: string) => void;
  savePreferences: (tz?: string, fmt?: string) => Promise<void>;
  formatDate: (date: string | Date) => string;
  formatTime: (date: string | Date | number) => string;
  formatDateTime: (date: string | Date) => string;
};

const PreferenceContext = createContext<PreferenceContextType | undefined>(undefined);

export function usePreference() {
  const ctx = useContext(PreferenceContext);
  if (!ctx) throw new Error("usePreference must be used within PreferenceProvider");
  return ctx;
}

export const PreferenceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, myProfile, updateProfile } = useAuth();

  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  // Raw state (may contain "automatic")
  const [timezoneState, setTimezoneState] = useState<string>(
    user?.time_zone || "automatic"
  );
  const [dateFormatState, setDateFormatState] = useState<string>(
    user?.date_format || "DD/MM/YYYY"
  );


  useEffect(() => {
    (async () => {
      try {
        const profile = await myProfile();
        const tz = profile?.time_zone || "automatic";
        const fmt = profile?.date_format || "DD/MM/YYYY";

        // Keep "automatic" in state; resolve only while formatting
        setTimezoneState(tz);
        setDateFormatState(fmt);
      } catch {
        // ignore
      }
    })();
  }, [user?.id]);


  // const resolveTimezone = (tz: string) => {
  //   if (!tz || tz === "automatic") return browserTz;
  //   return tz;
  // };

const resolveTimezone = (tz: string) => {
  const supportedValuesOf = (Intl as any).supportedValuesOf;
  const validTimezones =
    typeof supportedValuesOf === "function" ? supportedValuesOf("timeZone") : [browserTz];

  if (!tz || tz === "automatic") return browserTz;

  if (!validTimezones.includes(tz)) {
    console.warn("Invalid timezone detected; forcing fallback:", tz);
    return browserTz;
  }

  return tz;
};


  const resolvedTz = useMemo(() => resolveTimezone(timezoneState), [timezoneState, browserTz]);

  

  const savePreferences = async (tz?: string, fmt?: string) => {
    const nextTz = tz ?? timezoneState;   // may be "automatic"
    const nextFmt = fmt ?? dateFormatState;

    await updateProfile({
      time_zone: nextTz,
      date_format: nextFmt,
    });

    setTimezoneState(nextTz);
    setDateFormatState(nextFmt);

    // Update stored user
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const u = JSON.parse(stored);
        u.time_zone = nextTz;
        u.date_format = nextFmt;
        localStorage.setItem("user", JSON.stringify(u));
      }
    } catch {}
  };


  const fmtDate = (date: string | Date) =>
    dayjs(date).tz(resolvedTz).format(dateFormatState);

  const fmtTime = (date: string | Date | number) =>
    // dayjs(date).tz(resolvedTz).format("HH:mm");
  dayjs(date).tz(resolvedTz).format("hh:mm A");


  const fmtDateTime = (date: string | Date) =>
    dayjs(date)
      .tz(resolvedTz)
      .format(`${dateFormatState} HH:mm`);


  const value = useMemo<PreferenceContextType>(
    () => ({
      timezone: timezoneState,
      dateFormat: dateFormatState,
      setTimezone: setTimezoneState,
      setDateFormatPreference: setDateFormatState,
      savePreferences,
      formatDate: fmtDate,
      formatTime: fmtTime,
      formatDateTime: fmtDateTime,
    }),
    [timezoneState, dateFormatState, resolvedTz]
  );

  return <PreferenceContext.Provider value={value}>{children}</PreferenceContext.Provider>;
};